import React, { useState, useEffect, useRef } from 'react';
import { getElevenLabsVoices } from '../services/elevenlabsService';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { CloseIcon } from './icons/CloseIcon';
import { PlayCircleIcon } from './icons/PlayCircleIcon';
import { PauseCircleIcon } from './icons/PauseCircleIcon';

export const ElevenLabsVoiceModal = ({ onClose, onSelect }) => {
    const [voices, setVoices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [playingVoiceId, setPlayingVoiceId] = useState(null);
    const audioRef = useRef(null);

    const fetchVoices = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const fetchedVoices = await getElevenLabsVoices();
            setVoices(fetchedVoices);
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to fetch voices.";
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchVoices();
    }, []);

    const handlePlaySample = (voice) => {
        const audio = audioRef.current;
        if (!audio) return;

        if (playingVoiceId === voice.voice_id) {
            audio.pause();
            setPlayingVoiceId(null);
        } else {
            audio.src = voice.preview_url;
            audio.play().catch(e => console.error("Audio playback failed:", e));
            setPlayingVoiceId(voice.voice_id);
        }
    };

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleEnded = () => setPlayingVoiceId(null);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('ended', handleEnded);
        };
    }, []);

    const filteredVoices = voices.filter(voice => 
        voice.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose} role="dialog" aria-modal="true">
            <audio ref={audioRef} className="hidden" />
            <div className="bg-[#181820] border border-gray-700 rounded-2xl w-full max-w-lg h-[70vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                <header className="p-4 flex justify-between items-center border-b border-gray-700 shrink-0">
                    <h2 className="text-xl font-bold">Select an ElevenLabs Voice</h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-700"><CloseIcon className="w-6 h-6" /></button>
                </header>
                
                <div className="p-4 shrink-0">
                    <input
                        type="text"
                        placeholder="Search voices..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                </div>

                <main className="flex-grow overflow-y-auto px-4 pb-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <LoadingSpinner className="w-8 h-8" />
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-400 p-4 bg-red-500/10 rounded-md">
                            <p className="font-semibold">Error fetching voices:</p>
                            <p className="text-sm">{error}</p>
                            <button 
                                onClick={fetchVoices}
                                className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm"
                            >
                                Retry
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredVoices.map(voice => (
                                <div
                                    key={voice.voice_id}
                                    className="w-full flex items-center justify-between p-3 bg-gray-700/50 rounded-md transition-colors group"
                                >
                                    <div
                                        onClick={() => onSelect(voice.voice_id, voice.name)}
                                        className="flex-grow cursor-pointer"
                                        role="button"
                                        tabIndex={0}
                                        onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(voice.voice_id, voice.name)}
                                        aria-label={`Select voice ${voice.name}`}
                                    >
                                        <p className="font-semibold group-hover:text-purple-300 transition-colors">{voice.name}</p>
                                        <div className="text-xs text-gray-400 flex items-center gap-2 flex-wrap mt-1">
                                            <span className="bg-gray-800 px-1.5 py-0.5 rounded-full">{voice.category}</span>
                                            {Object.entries(voice.labels || {}).map(([key, value]) => (
                                               value && <span key={key} className="bg-gray-600 px-1.5 py-0.5 rounded-full">{value}</span>
                                            ))}
                                        </div>
                                    </div>
                                     <button
                                        onClick={() => handlePlaySample(voice)}
                                        className="p-1 text-gray-300 hover:text-white rounded-full flex-shrink-0 ml-4"
                                        aria-label={playingVoiceId === voice.voice_id ? `Pause sample for ${voice.name}` : `Play sample for ${voice.name}`}
                                    >
                                        {playingVoiceId === voice.voice_id ? (
                                            <PauseCircleIcon className="w-6 h-6 text-purple-400" />
                                        ) : (
                                            <PlayCircleIcon className="w-6 h-6" />
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};
